import type {
  Availability,
  BootstrapResponse,
  Fill,
  Override,
  SubKind,
  UsageRow,
} from '@topspin/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  path: string,
  opts: { key: string; playerId?: string | null; method?: string; body?: unknown },
): Promise<T> {
  const headers: Record<string, string> = { 'X-Topspin-Key': opts.key };
  if (opts.playerId) headers['X-Topspin-Player'] = opts.playerId;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  const res = await fetch(`/api${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
  });

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as unknown) : {};
  if (!res.ok) {
    const body = parsed as { error?: string; message?: string };
    throw new ApiError(res.status, body.error ?? 'error', body.message ?? 'Something went wrong.');
  }
  return parsed as T;
}

export function fetchBootstrap(key: string, playerId: string | null): Promise<BootstrapResponse> {
  return request<BootstrapResponse>('/bootstrap', { key, playerId });
}

export interface OutInput {
  seasonId: string;
  groupId: string;
  date: string;
  outPlayerId: string;
  subKind: SubKind;
  subPlayerId?: string;
  subName?: string;
}

const outsPath = (i: { seasonId: string; groupId: string; date: string }) =>
  `/seasons/${i.seasonId}/groups/${i.groupId}/sessions/${i.date}/outs`;

export function createOut(
  key: string,
  playerId: string | null,
  input: OutInput,
): Promise<{ override: Override }> {
  const { seasonId, groupId, date, ...body } = input;
  return request(outsPath({ seasonId, groupId, date }), {
    key,
    playerId,
    method: 'POST',
    body,
  });
}

export function updateOut(
  key: string,
  playerId: string | null,
  input: OutInput & { expectedUpdatedAt: string },
): Promise<{ override: Override }> {
  const { seasonId, groupId, date, outPlayerId, ...body } = input;
  return request(`${outsPath({ seasonId, groupId, date })}/${outPlayerId}`, {
    key,
    playerId,
    method: 'PATCH',
    body,
  });
}

export function deleteOut(
  key: string,
  playerId: string | null,
  input: { seasonId: string; groupId: string; date: string; outPlayerId: string },
): Promise<void> {
  return request(`${outsPath(input)}/${input.outPlayerId}`, {
    key,
    playerId,
    method: 'DELETE',
  });
}

export interface FillInput {
  seasonId: string;
  groupId: string;
  date: string;
  name: string;
}

/**
 * Records a name against one of a short-handed night's empty slots (§9.1). Which slot is the
 * server's business — the client only ever has a name and a night that is short.
 */
export function createFill(
  key: string,
  playerId: string | null,
  input: FillInput,
): Promise<{ fill: Fill }> {
  const { seasonId, groupId, date, name } = input;
  return request(`/seasons/${seasonId}/groups/${groupId}/sessions/${date}/fills`, {
    key,
    playerId,
    method: 'POST',
    body: { name },
  });
}

export interface AvailabilityInput {
  seasonId: string;
  groupId: string;
  date: string;
  playerId: string;
  available: boolean;
}

export function setAvailability(
  key: string,
  playerId: string | null,
  input: AvailabilityInput,
): Promise<{ availability: Availability }> {
  const { seasonId, groupId, date, playerId: subject, available } = input;
  return request(
    `/seasons/${seasonId}/groups/${groupId}/sessions/${date}/availability/${subject}`,
    { key, playerId, method: 'PUT', body: { available } },
  );
}

export interface CalendarSubscriptionMinted {
  url: string;
  webcalUrl: string;
  groupIds: string[];
  createdAt: string;
}

/**
 * Mint this player's calendar link, covering every group ID the device holds — one header
 * carries one, so the rest go in the body. Kills whatever subscription the player had, which is
 * what makes "add to this device instead" and "update to include Thursday" the same one button.
 */
export function mintCalendarSubscription(
  key: string,
  playerId: string | null,
  keys: string[],
): Promise<CalendarSubscriptionMinted> {
  return request('/calendar-subscriptions', { key, playerId, method: 'POST', body: { keys } });
}

/** Revokes the player's subscription whichever device minted it. `204` even when there was none. */
export function stopCalendarSubscription(key: string, playerId: string | null): Promise<void> {
  return request('/calendar-subscriptions', { key, playerId, method: 'DELETE' });
}

export function fetchReminder(
  key: string,
  playerId: string | null,
  input: { seasonId: string; groupId: string; date: string },
): Promise<{ date: string; subject: string; body: string }> {
  return request(
    `/seasons/${input.seasonId}/groups/${input.groupId}/reminder?date=${input.date}`,
    { key, playerId },
  );
}

export interface UsageResponse {
  groupId: string;
  name: string;
  rows: UsageRow[];
}

/** One group's usage report (§5, §9). Admin only, and 403s for anyone else. */
export function fetchUsage(
  key: string,
  playerId: string | null,
  input: { seasonId: string; groupId: string },
): Promise<UsageResponse> {
  return request(`/seasons/${input.seasonId}/groups/${input.groupId}/usage`, { key, playerId });
}
